import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import dompurify from 'dompurify';
import React, { forwardRef, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AxiosConfig from '../../../../../../../config/AxiosConfig';
import ArrowReturnIcon from '../../../../../svg-icons/ArrowReturnIcon';
import DesiredShippingDateIcon from '../../../../../svg-icons/DesiredShippingDateIcon';
import { getFormattedNote } from '../utils';
import { setSnackBar } from '../../../../../../states/app/staticSlice';
import { useDispatch } from 'react-redux';

const DateSelectInput = forwardRef(({ value, onClick }, ref) => (
  <TextField
    variant="outlined"
    onClick={onClick}
    value={value}
    ref={ref}
    size="small"
    fullWidth
    slotProps={{
      input: {
        startAdornment: (
          <InputAdornment position="start">
            <CalendarTodayIcon />
          </InputAdornment>
        ),
      },
    }}
  />
));

const DesiredShippingDateForm = ({ handleShowComponent, ticket, profile }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [desiredShippingDate, setDesiredShippingDate] = useState({
    displayedDate: null,
    formattedDate: null,
    selectedDate: null,
  });
  const { order } = ticket;
  const [formData, setFormData] = useState({
    comment: '',
    formattedNote: '',
  });

  const years = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() + i
  );
  const months = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];

  const handleConfirm = () => {
    if (desiredShippingDate?.selectedDate) {
      const formatted = format(desiredShippingDate?.selectedDate, 'dd/MM/yyyy');
      setDesiredShippingDate((state) => ({
        ...state,
        formattedDate: formatted,
      }));
      const formattedNote = getFormattedNote(
        profile?.firstName + ' ' + profile?.lastName,
        formData.comment,
        desiredShippingDate.formattedDate,
        'shippingDate'
      );
      setFormData((state) => ({ ...state, formattedNote }));
      setIsDialogOpen(true);
    }
  };

  const handleUpdateShippingDate = async () => {
    setIsLoading(true);
    try {
      let response;
      await AxiosConfig.create('put', `/orders/${order?._id}/shippingDate/`)
        .call({
          date: desiredShippingDate.formattedDate,
          ticketId: ticket?._id,
          note: formData.formattedNote,
          userId: profile?._id,
        })
        .exit((res) => (response = res?.data))
        .catch((error) => {
          if (error && error?.status > 300) {
            const messageError = `Error: ${error?.status} - ${error?.statusText}: DesiredShippingDateForm`;
            dispatch(
              setSnackBar({ open: true, message: messageError, color: 'error' })
            );
          } else {
            dispatch(setSnackBar({ open: false, message: null, color: null }));
          }
        });

      if (!response) {
        throw new Error('Failed to update shipping date');
      }

      setIsDialogOpen(false);
      handleShowComponent(null);
    } catch (error) {
      console.error('Failed to update shipping date:', error);
      setIsDialogOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommentChange = (e) => {
    const sanitizedValue = dompurify.sanitize(e.target.value);
    setFormData((prev) => ({ ...prev, comment: sanitizedValue }));
  };

  useEffect(() => {
    if (desiredShippingDate?.selectedDate) {
      const displayedDate = format(
        desiredShippingDate?.selectedDate,
        'EEEE d MMMM yyyy',
        {
          locale: fr,
        }
      );
      const formattedDate = format(
        desiredShippingDate?.selectedDate,
        'dd/MM/yyyy'
      );
      setDesiredShippingDate((state) => ({
        ...state,
        displayedDate: displayedDate,
        formattedDate: formattedDate,
      }));
    }
  }, [desiredShippingDate?.selectedDate]);

  return (
    <>
      <Card
        elevation={5}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          padding: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <DesiredShippingDateIcon fontSize="small" />
            <Typography color="primary">
              Date d&apos;expédition souhaitée
            </Typography>
          </Box>
          <Button
            onClick={() => handleShowComponent(null)}
            variant="outlined"
            size="small"
            sx={{ position: 'absolute', top: 16, left: 16 }}
          >
            <ArrowReturnIcon fontSize="small" />
            <span style={{ marginLeft: 4, fontSize: '0.9em' }}>Retour</span>
          </Button>
        </Box>
      </Card>

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          py: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            maxWidth: '600px',
          }}
        >
          <Typography>
            Sélectionnez une nouvelle date d&apos;expédition souhaitée pour la
            commande
          </Typography>
          <ReactDatePicker
            selected={desiredShippingDate?.selectedDate}
            onChange={(date) =>
              setDesiredShippingDate((state) => ({
                ...state,
                selectedDate: date,
              }))
            }
            dateFormat="dd/MM/yyyy"
            locale={fr}
            inline
            excludeDateIntervals={[
              {
                start: new Date(0),
                end: order?.dates?.shippingDate
                  ? new Date(order?.dates?.shippingDate)
                  : new Date(),
              },
            ]}
            customInput={<DateSelectInput />}
            renderCustomHeader={({ date, changeYear, changeMonth }) => (
              <div
                style={{
                  margin: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                <select
                  value={date.getFullYear()}
                  onChange={({ target: { value } }) =>
                    changeYear(Number(value))
                  }
                >
                  {years.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                <select
                  value={months[date.getMonth()]}
                  onChange={({ target: { value } }) =>
                    changeMonth(months.indexOf(value))
                  }
                >
                  {months.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
          />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            {desiredShippingDate?.formattedDate ? (
              <>
                <Typography variant="subtitle2">
                  La prochaine date d&apos;expédition souhaitée sera le
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {desiredShippingDate.displayedDate}
                </Typography>
              </>
            ) : (
              <Typography variant="subtitle2">
                Choisissez une date d&apos;expédition souhaitée
              </Typography>
            )}
          </Box>
          <FormControl fullWidth>
            <Typography sx={{ fontSize: '0.8em', mb: 1 }}>
              Commentaire (optionnel)
            </Typography>
            <TextField
              multiline
              rows={4}
              value={formData.comment}
              onChange={handleCommentChange}
              placeholder="Détaillez la raison de la modification..."
              size="small"
            />
          </FormControl>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleConfirm}
            sx={{ width: '50%', mt: 2 }}
            disabled={!desiredShippingDate?.selectedDate || isLoading}
          >
            Modifier la date d&apos;expédition
          </Button>
        </Box>
      </Box>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir modifier la date d&apos;expédition
            souhaitée au {desiredShippingDate?.displayedDate} ?
          </DialogContentText>
          <Typography
            sx={{
              fontStyle: 'italic',
              padding: 2,
              border: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 1,
              mt: 1,
            }}
          >
            {formData.formattedNote}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Annuler
          </Button>
          <Button
            isLoading={isLoading}
            onClick={handleUpdateShippingDate}
            color="primary"
            variant="contained"
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DesiredShippingDateForm;
